/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo } from "react";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import ReactStreetview from "react-streetview";
import CommuterScoreTab from "./CommuterScoreTab";
import LocationHighlightsTab from "./LocationHighlightsTab";
import AerialImageCarousel from "./AerialImageCarousel";
import { getCommuterScore } from "@/api/PDP.js";
import "./location-market-context.css";

const TABS = {
  COMMUTER_SCORE: "commuter-score",
  LOCATION_HIGHLIGHTS: "location-highlights",
};

const GOOGLE_MAPS_LIBRARIES = ["places"];

const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "658px",
};

/**
 * StreetViewPanel - Uses the same ReactStreetview logic as GalleryStreetView
 */
const StreetViewPanel = ({ coordinates, gmapKey }) => {
  const [error, setError] = useState(null);
  const [streetViewLoaded, setStreetViewLoaded] = useState(false);

  useEffect(() => {
    if (coordinates?.lat && coordinates?.lng) {
      const sv = new window.google.maps.StreetViewService();
      sv.getPanorama({ location: coordinates, radius: 50 }, (data, status) => {
        setStreetViewLoaded(true);
        if (status !== "OK") {
          setError("Street View is not available for this property.");
        }
      });
    } else {
      setStreetViewLoaded(true);
      setError("Street View is not available for this property.");
    }
  }, [coordinates]);

  if (error) {
    return (
      <div className="location-market-context__streetview-error">
        <b>{error}</b>
      </div>
    );
  }

  if (!streetViewLoaded) {
    return (
      <div className="location-market-context__map-loading">
        <div className="commuter-score-tab__spinner" aria-hidden="true" />
        <span>Loading Street View...</span>
      </div>
    );
  }

  return (
    <div className="location-market-context__streetview">
      <ReactStreetview
        apiKey={gmapKey}
        streetViewPanoramaOptions={{
          position: coordinates,
          pov: { heading: 100, pitch: 0 },
          zoom: 1,
        }}
      />
    </div>
  );
};

/**
 * Inner component that renders after Google Maps key is available.
 * useLoadScript is called here with a guaranteed non-empty key.
 */
const LocationMarketContextInner = ({ pdpInitData, cmsData }) => {
  const [activeTab, setActiveTab] = useState(TABS.COMMUTER_SCORE);
  const [mapViewType, setMapViewType] = useState("roadmap"); // roadmap, satellite, streetview
  const [scoreData, setScoreData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showSatelliteFallback, setShowSatelliteFallback] = useState(false);

  const latitude = pdpInitData?.propertyDetails?.address?.latitude;
  const longitude = pdpInitData?.propertyDetails?.address?.longitude;
  const googleMapApiKey = cmsData?.pdpMapsConfig?.googleMapApiKey;

  const detailedAttributes = pdpInitData?.propertyDetails?.detailedAttributes;
  const showMap = detailedAttributes?.showMapStreetView === "Y";
  const showCommuterScore = detailedAttributes?.showCommuterScoreLocHighlights === "Y";

  // useLoadScript with a guaranteed valid key (parent ensures this)
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [geocodedCoords, setGeocodedCoords] = useState(null);

  const coordinates = useMemo(() => {
    // Use lat/lng from data if available
    if (latitude && longitude) return { lat: latitude, lng: longitude };
    // Fall back to geocoded coordinates
    if (geocodedCoords) return geocodedCoords;
    return null;
  }, [latitude, longitude, geocodedCoords]);

  const aerialImages = useMemo(() => {
    const imageDetails = pdpInitData?.propertyDetails?.imageDetails;
    if (!Array.isArray(imageDetails)) return [];

    return imageDetails
      .filter((img) => img.aerialView === 1)
      .sort((a, b) => {
        const seqA = a.imageDisplaySequence != null ? Number(a.imageDisplaySequence) : Infinity;
        const seqB = b.imageDisplaySequence != null ? Number(b.imageDisplaySequence) : Infinity;
        return seqA - seqB;
      })
      .map((img) => ({
        url: img.pdpBigImage || img.imageStoragePath,
        alt: img.imageName || 'Aerial view of the property',
        sequence: img.imageDisplaySequence,
      }));
  }, [pdpInitData?.propertyDetails?.imageDetails]);

  // Geocode address if lat/lng not available
  useEffect(() => {
    if (latitude && longitude) return; // Already have coords
    if (!isLoaded) return;

    const address = pdpInitData?.propertyDetails?.address;
    if (!address) return;

    const { streetNumber, streetName, city, state, zip } = address;
    const fullAddress = [streetNumber, streetName, city, state, zip].filter(Boolean).join(' ');
    if (!fullAddress.trim()) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: fullAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        setGeocodedCoords({ lat: location.lat(), lng: location.lng() });
      } else {
        console.error('[LocationMarketContext] Geocoding failed:', status);
      }
    });
  }, [isLoaded, latitude, longitude, pdpInitData?.propertyDetails?.address]);

  // Debug: log loading state
  // useEffect(() => {
  //   console.log('[LocationMarketContext] isLoaded:', isLoaded, 'apiKey:', googleMapApiKey ? 'present' : 'missing', 'coordinates:', coordinates);
  // }, [isLoaded, googleMapApiKey, coordinates]);

  // Fetch commuter score + location highlights from backend API
  useEffect(() => {
    if (!showCommuterScore) return;
    const propertyId = pdpInitData?.propertyDetails?.propertyId;
    if (!propertyId) return;

    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const result = await getCommuterScore(propertyId);

        if (cancelled) return;

        if (!result) {
          setHasError(true);
          setIsLoading(false);
          return;
        }

        setScoreData(result);
        setIsLoading(false);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch commuter score:", error);
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [pdpInitData?.propertyDetails?.propertyId, showCommuterScore]);

  const handleLearnMoreClick = () => {
    const faqSection = document.getElementById("faq-section");
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="location-market-context">
      <div className="location-market-context__header">
        <h2 className="location-market-context__heading">
          Location &amp; Market Context
        </h2>

        {/* Map View Type Tabs */}
        {showMap && (
          <div className="location-market-context__map-tabs">
            {aerialImages.length > 0 && (
              <button
                type="button"
                className={`location-market-context__map-tab-btn ${mapViewType === 'satellite' ? 'location-market-context__map-tab-btn--active' : ''}`}
                onClick={() => setMapViewType('satellite')}
              >
                Aerial View
              </button>
            )}
            <button
              type="button"
              className={`location-market-context__map-tab-btn ${mapViewType === 'roadmap' ? 'location-market-context__map-tab-btn--active' : ''}`}
              onClick={() => setMapViewType('roadmap')}
            >
              Map View
            </button>
            <button
              type="button"
              className={`location-market-context__map-tab-btn ${mapViewType === 'streetview' ? 'location-market-context__map-tab-btn--active' : ''}`}
              onClick={() => setMapViewType('streetview')}
            >
              Street View
            </button>
          </div>
        )}
      </div>

      <div
        className={`location-market-context__layout${
          showMap && showCommuterScore ? '' : ' location-market-context__layout--single'
        }`}
        role="region"
        aria-label="Location map and commuter score"
      >

        {/* Left Panel: Map */}
        {showMap && (
          <div className="location-market-context__map-panel">

            {/* Map Content */}
            {isLoaded && coordinates ? (
              mapViewType === 'satellite' && aerialImages.length > 0 && !showSatelliteFallback ? (
                <AerialImageCarousel
                  images={aerialImages}
                  onAllImagesFailed={() => setShowSatelliteFallback(true)}
                />
              ) : mapViewType === 'streetview' ? (
                <StreetViewPanel coordinates={coordinates} gmapKey={googleMapApiKey} />
              ) : (
                <GoogleMap
                  mapContainerStyle={MAP_CONTAINER_STYLE}
                  center={coordinates}
                  zoom={15}
                  mapTypeId={mapViewType === 'satellite' ? 'satellite' : 'roadmap'}
                  options={{
                    zoomControl: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                >
                  <Marker
                    position={coordinates}
                    icon={{
                      url: "/assets/images/pdp/pin.png",
                      scaledSize: new window.google.maps.Size(30, 38),
                    }}
                  />
                </GoogleMap>
              )
            ) : (
              <div className="location-market-context__map-loading">
                <div className="commuter-score-tab__spinner" aria-hidden="true" />
                <span>Loading map...</span>
              </div>
            )}
          </div>
        )}

        {/* Right Panel: Tabs */}
        {showCommuterScore && (
        <div className="location-market-context__tabs-panel">
          <div className="location-market-context__tab-nav" role="tablist">
            <button
              type="button"
              role="tab"
              id="tab-commuter-score"
              className={`location-market-context__tab-btn ${
                activeTab === TABS.COMMUTER_SCORE
                  ? "location-market-context__tab-btn--active"
                  : ""
              }`}
              aria-selected={activeTab === TABS.COMMUTER_SCORE}
              aria-controls="panel-commuter-score"
              onClick={() => setActiveTab(TABS.COMMUTER_SCORE)}
            >
              Commuter Score
            </button>
            <button
              type="button"
              role="tab"
              id="tab-location-highlights"
              className={`location-market-context__tab-btn ${
                activeTab === TABS.LOCATION_HIGHLIGHTS
                  ? "location-market-context__tab-btn--active"
                  : ""
              }`}
              aria-selected={activeTab === TABS.LOCATION_HIGHLIGHTS}
              aria-controls="panel-location-highlights"
              onClick={() => {
                setActiveTab(TABS.LOCATION_HIGHLIGHTS);
              }}
            >
              Location Highlights
            </button>
          </div>

          <div className="location-market-context__tab-content">
            <div
              id="panel-commuter-score"
              role="tabpanel"
              aria-labelledby="tab-commuter-score"
              style={{ display: activeTab === TABS.COMMUTER_SCORE ? 'block' : 'none' }}
            >
              <CommuterScoreTab
                score={scoreData?.score ?? 0}
                ratingTier={scoreData?.ratingTier ?? "Poor"}
                tierColor={scoreData?.tierColor ?? "#9ca3af"}
                nearestHighways={scoreData?.nearestHighways ?? []}
                isLoading={isLoading}
                hasError={hasError}
                onLearnMoreClick={handleLearnMoreClick}
              />
            </div>

            <div
              id="panel-location-highlights"
              role="tabpanel"
              aria-labelledby="tab-location-highlights"
              style={{ display: activeTab === TABS.LOCATION_HIGHLIGHTS ? 'block' : 'none' }}
            >
              <LocationHighlightsTab
                airport={scoreData?.nearestAirport ?? null}
                infrastructure={scoreData?.publicInfrastructure ?? []}
                spots={scoreData?.noteworthySpots ?? []}
                spotsTotal={scoreData?.noteworthySpotsTotal ?? 0}
                isLoading={isLoading}
                hasError={hasError}
              />
            </div>
          </div>
        </div>
        )}
      </div>

      <p className="location-market-context__disclaimer">
        All location scores and points of interest are provided for general informational purposes only and do not guarantee accuracy or completeness. Users should independently verify local conditions and amenities.
      </p>
    </div>
  );
};

/**
 * Wrapper that only renders the inner component once cmsData has the Google Maps API key.
 * This ensures useLoadScript always receives a valid key on first render.
 */
const LocationMarketContext = ({ pdpInitData, cmsData }) => {
  if (!cmsData?.pdpMapsConfig?.googleMapApiKey) {
    return (
      <div className="location-market-context">
        <h2 className="location-market-context__heading">
          Location &amp; Market Context
        </h2>
        <div className="location-market-context__layout">
          <div className="location-market-context__map-panel">
            <div className="location-market-context__map-placeholder">
              <img
                src="/assets/images/srp/map_placeholder.png"
                alt="Map loading"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          </div>
          <div className="location-market-context__tabs-panel" />
        </div>
      </div>
    );
  }

  return <LocationMarketContextInner pdpInitData={pdpInitData} cmsData={cmsData} />;
};

export default LocationMarketContext;
